<template>
    <div>
        <div class="under-header p-10" id="main-section" :style="backgroundImageStyle">
            <div class="mobile-background">
                <h1 class="font-medium layoutHeader blur">{{ page?.altTitle || page?.title || 'Untitled Page' }}</h1>
                <Splitter class="featuredSplitter">
                    <SplitterPanel class="isotopesPanel" :size="60" :size-small="100">
                        <div class="pr-4">
                            <div v-if="page?.wistiaEmbed && isDirectVideoUrl(page.wistiaEmbed)" class="wistia-embed">
                                <OfflineVideo :src="resolveVideoSrc(page.wistiaEmbed)" />
                            </div>
                            <div v-else-if="page?.wistiaEmbed" class="wistia-embed">
                                <iframe :src="getWistiaEmbedUrl(page.wistiaEmbed)" webkit-playsinline="true" playsinline="true" allowfullscreen="false" frameborder="0"></iframe>
                            </div>
                            <div v-else-if="page?.contentImage" class="wistia-embed">
                                <img :src="resolveImageUrl(page.contentImage)" :alt="`${page?.title || 'Content'} image`">
                            </div>
                            <div v-else class="text-500 text-center p-5">
                                <p>No content available</p>
                            </div>
                        </div>
                    </SplitterPanel>
                    <SplitterPanel class="isotopesPanel" :size="40" :size-small="100">
                        <div class="pl-4">
                            <div v-if="page?.tabbedContent && page.tabbedContent.length > 0">
                                <Tabs :value="0">
                                    <TabList>
                                        <Tab v-for="(tab, idx) in page.tabbedContent" :key="tab.title" :value="idx">{{ tab.title }}</Tab>
                                    </TabList>
                                    <TabPanels>
                                        <TabPanel v-for="(tab, idx) in page.tabbedContent" :key="tab.title" :value="idx">
                                            <div v-html="processedContent(tab.content)"></div>
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            </div>
                            <div v-else-if="page?.content" class="page-content large">
                                <div v-html="processedContent(page.content)"></div>
                                <div v-if="page?.additionalContent && page.additionalContent.length > 0" class="mt-4 desktop-only">
                                    <button 
                                        @click="scrollToAdditionalContent(0)"
                                        class="cta cta--isotopes cursor-pointer text-white px-4 py-2 rounded-lg flex items-center"
                                        :aria-label="`View ${page.additionalContent[0]?.buttonText || 'more content'}`"
                                        :title="page.additionalContent[0]?.buttonText || 'View More'"
                                    >
                                        {{ page.additionalContent[0]?.buttonText || 'View More' }}
                                    </button>
                                </div>
                            </div>
                            <div v-else class="text-500 text-center p-5">
                                <p>No content available</p>
                            </div>
                        </div>
                    </SplitterPanel>
                </Splitter>
            </div>
        </div>
        <div v-if="page?.additionalContent" :style="backgroundImageStyle">
            <div class="mobile-background">
                <div v-for="(addition, idx) in page.additionalContent" :key="idx">
                    <div :id="idx + '-additional-content'" class="under-header additional p-10">
                        <div class="scrollTop desktop-only">
                            <div :class="addition?.pageTitle ? 'flex justify-between items-center mb-4' : 'flex justify-end items-center mb-4'">
                                <div v-if="addition?.pageTitle" class="flex flex-column headers">
                                    <h2 class="font-medium layoutHeader blur">{{ addition?.pageTitle || 'Untitled Page' }}</h2>
                                </div>
                                <button 
                                    @click="scrollToTopSection" 
                                    class="cta cta--isotopes cursor-pointer text-white px-4 py-2 rounded-lg flex items-center"
                                    aria-label="Scroll back to About Us section"
                                    title="Back to About Us"
                                >
                                    <i class="pi pi-arrow-up mr-5"></i>
                                    Back to About Us
                                </button>
                            </div>
                        </div>
                        <Splitter class="featuredSplitter">
                            <SplitterPanel class="isotopesPanel" :size="60" :size-small="100">
                                <div class="pr-4">
                                    <div v-if="addition.wistiaEmbed && isDirectVideoUrl(addition.wistiaEmbed)" class="wistia-embed">
                                        <OfflineVideo :src="resolveVideoSrc(addition.wistiaEmbed)" />
                                    </div>
                                    <div v-else-if="addition.wistiaEmbed" class="wistia-embed">
                                        <iframe :src="getWistiaEmbedUrl(addition.wistiaEmbed)" frameborder="0" allowfullscreen="false" webkit-playsinline playsinline></iframe>
                                    </div>
                                    <div v-else-if="additionalContentWithMedia[idx]?.contentImage" class="wistia-embed">
                                        <img :src="resolveImageUrl(additionalContentWithMedia[idx].contentImage)" :alt="`${addition?.pageTitle || 'Content'} image`">
                                    </div>
                                    <div v-else class="text-500 text-center p-5">
                                        <p>No content available</p>
                                    </div>
                                </div>
                            </SplitterPanel>
                            <SplitterPanel class="isotopesPanel" :size="40" :size-small="100">
                                <div class="pl-4">
                                    <div v-if="addition.content" class="page-content">
                                        <div v-html="processedContent(addition.content)"></div>
                                    </div>
                                    <div v-else class="text-500 text-center p-5">
                                        <p>No content available</p>
                                    </div>
                                </div>
                            </SplitterPanel>
                        </Splitter>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import OfflineVideo from '../components/OfflineVideo.vue';
import { ref, watch, computed } from 'vue';
import { useCommonStyles } from '../composables/useCommonStyles';
import { useMediaHelpers } from '../composables/useMediaHelpers.js';
import { processContent } from '../utils/markdown';
import ApiService from '../services/ApiService.js';

const props = defineProps({
  page: Object,
  settings: Object
});

const { isDirectVideoUrl, resolveVideoSrc, resolveImageUrl, getWistiaEmbedUrl } = useMediaHelpers();

// Use common styles composable
const { backgroundImageStyle } = useCommonStyles(props);

// Process second content area with markdown
const processedContent = (content) => {
  return content ? processContent(content) : '';
};

// Function to scroll to additional content section
const scrollToAdditionalContent = (index) => {
  const targetElement = document.getElementById(`${index}-additional-content`);
  if (targetElement) {
    targetElement.scrollIntoView({ behavior: 'smooth' });
  }
};

const scrollToTopSection = () => {
  const isotopeElement = document.getElementById('main-section');
  if (isotopeElement) {
    isotopeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
};

// Reactive data for component media
const additionalContentWithMedia = ref([]);
const isLoadingComponentMedia = ref(false);

// Function to fetch component media using cached API service
const fetchComponentMedia = async () => {
  if (!props.page?.additionalContent || props.page.additionalContent.length === 0) {
    return;
  }

  isLoadingComponentMedia.value = true;
  
  try {
    const response = await ApiService.getPageComponents(props.page.slug, 'additionalContent');
    if (response && response.data) {
      additionalContentWithMedia.value = response.data;
    }
  } catch (error) {
    console.error('Error fetching component media:', error);
  } finally {
    isLoadingComponentMedia.value = false;
  }
};

// Watch for page changes and fetch component media
watch(() => props.page, (newPage) => {
  if (newPage?.additionalContent && newPage.additionalContent.length > 0) {
    fetchComponentMedia();
  }
}, { immediate: true });
</script>
